/**
 * Pure logic for the editor billing-details screen (spec #298) - kept out of
 * the JSX so it is Vitest-able in under a second, same reasoning as
 * `availability.ts` beside the availability screen.
 *
 * Two payout methods: an IBAN, or a bank account without IBAN (bank name +
 * account number, for countries with no IBANs). SWIFT/BIC is optional in
 * both, checked for its ISO 9362 shape when given.
 *
 * IBAN validation mirrors the server's own check
 * (`editor-billing-details-routes.ts#isValidIban`) so Save can fail fast, but
 * the server validates again regardless (CLAUDE.md "der Client re-derived
 * keine Server-Regel" - this is the client's own copy of a check the server
 * enforces anyway, not the one place it lives) and the company DB validates
 * the IBAN a third time, for real (country-specific length, not just the
 * checksum this can check offline).
 *
 * @module web/screens/billingDetails
 */

export type VatStatus = "small_business" | "vat_registered" | "none";

/** `iban`, or `bank_account` = a bank account without IBAN (bank name + account number) for countries with no IBANs. */
export type PayoutMethod = "iban" | "bank_account";

export interface BillingDetailsDraft {
  legalName: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: string;
  payoutMethod: PayoutMethod;
  iban: string;
  bankName: string;
  accountNumber: string;
  bic: string;
  accountHolder: string;
  taxNumber: string;
  vatId: string;
  vatStatus: VatStatus | "";
}

export function emptyBillingDetailsDraft(): BillingDetailsDraft {
  return {
    legalName: "", addressLine1: "", addressLine2: "", postalCode: "", city: "", country: "",
    payoutMethod: "iban", iban: "", bankName: "", accountNumber: "", bic: "", accountHolder: "", taxNumber: "", vatId: "", vatStatus: "",
  };
}

/** Strips spaces and uppercases - the canonical on-the-wire IBAN form. */
export function normalizeIban(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

/** ISO 7064 mod-97-10 - the same checksum every bank validates an IBAN with. */
export function isValidIban(raw: string): boolean {
  const iban = normalizeIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(`${remainder}${numeric.slice(i, i + 7)}`) % 97;
  }
  return remainder === 1;
}

/** Upper-case, spaces/dashes/dots/slashes removed - the server's and the DB's own normalisation. */
export function normalizeAccountNumber(raw: string): string {
  return raw.replace(/[\s./-]+/g, "").toUpperCase();
}

/** 4 to 34 letters/digits with at least one digit, after normalising (mirrors the server and `company.bank_account_number_valid`). */
export function isValidAccountNumber(raw: string): boolean {
  const n = normalizeAccountNumber(raw);
  return /^[A-Z0-9]{4,34}$/.test(n) && /\d/.test(n);
}

/** Upper-case, spaces removed. */
export function normalizeBic(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

/** ISO 9362 SWIFT/BIC: 8 or 11 characters (bank, country, location, optional branch). */
export function isValidBic(raw: string): boolean {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizeBic(raw));
}

/** "•••• 3000" - all but the last 4 characters hidden, the profile's default IBAN display. */
export function maskIban(raw: string): string {
  const iban = normalizeIban(raw);
  if (iban.length <= 4) return iban;
  return `•••• ${iban.slice(-4)}`;
}

export interface BillingDetailsFieldErrors {
  legalName?: string;
  addressLine1?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  accountHolder?: string;
  iban?: string;
  bankName?: string;
  accountNumber?: string;
  bic?: string;
}

/** Every field error for this draft, by field name - empty object means it is ready to save. */
export function validateBillingDetailsDraft(d: BillingDetailsDraft): BillingDetailsFieldErrors {
  const errors: BillingDetailsFieldErrors = {};
  if (!d.legalName.trim()) errors.legalName = "Legal name is required";
  if (!d.addressLine1.trim()) errors.addressLine1 = "Address is required";
  if (!d.postalCode.trim()) errors.postalCode = "Postal code is required";
  if (!d.city.trim()) errors.city = "City is required";
  if (!d.country.trim()) errors.country = "Country is required";
  if (!d.accountHolder.trim()) errors.accountHolder = "Account holder is required";
  if (d.payoutMethod === "iban") {
    if (!d.iban.trim()) errors.iban = "IBAN is required";
    else if (!isValidIban(d.iban)) errors.iban = "That doesn't look like a valid IBAN";
  } else {
    const bank = d.bankName.trim();
    if (!bank) errors.bankName = "Bank name is required";
    else if (bank.length < 2 || bank.length > 140) errors.bankName = "That doesn't look like a bank name";
    if (!d.accountNumber.trim()) errors.accountNumber = "Account number is required";
    else if (!isValidAccountNumber(d.accountNumber)) errors.accountNumber = "That doesn't look like an account number";
    else if (isValidIban(normalizeAccountNumber(d.accountNumber))) errors.accountNumber = "That is an IBAN - choose IBAN above";
  }
  if (d.bic.trim() && !isValidBic(d.bic)) errors.bic = "That doesn't look like a SWIFT/BIC code";
  return errors;
}
